
import { useState, useEffect, useRef } from 'react';
import { supabase } from "@/lib/supabase/client";
import { Customer, CustomerStats } from './types';

export const useCustomerStats = (customers: Customer[], open: boolean) => {
  const [customersWithStats, setCustomersWithStats] = useState<Customer[]>([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const isFetchingRef = useRef(false);
  const previousCustomersRef = useRef<Customer[]>([]);
  
  useEffect(() => {
    // Check if customers have changed
    const customersChanged = customers.length !== previousCustomersRef.current.length;
    previousCustomersRef.current = customers;
    
    const fetchCustomerStats = async () => {
      // Vérifier si le dialogue est ouvert et s'il y a des clients à traiter
      if (!open || customers.length === 0 || isFetchingRef.current) return;
      
      // If we already have stats and nothing changed, don't fetch again
      if (customersWithStats.length > 0 && !customersChanged) return;
      
      isFetchingRef.current = true;
      setLoadingStats(true);
      try {
        // Create a copy of customers with stats initialized
        const enhancedCustomers = [...customers].map(customer => ({
          ...customer,
          stats: {
            orders_count: 0,
            total_spent: 0,
            delivered_products: 0,
            returned_products: 0
          } as CustomerStats
        }));

        // Pour limiter les requêtes, ne traiter que jusqu'à 10 clients maximum
        const clientsToProcess = enhancedCustomers.slice(0, 10);

        // For each customer, fetch their orders count
        for (const customer of clientsToProcess) {
          // Get order count
          const { data: orderData, error: orderError } = await supabase
            .from('orders')
            .select('id, total_amount')
            .eq('customer_id', customer.id);
            
          if (!orderError && orderData) {
            const orders_count = orderData.length;
            const total_spent = orderData.reduce((sum, order) => sum + (parseFloat(order.total_amount) || 0), 0);
            
            if (customer.stats) {
              customer.stats.orders_count = orders_count;
              customer.stats.total_spent = total_spent;
            }
            
            // For each order, get delivered and returned products
            let delivered = 0;
            let returned = 0;
            
            for (const order of orderData) {
              const { data: itemsData, error: itemsError } = await supabase
                .from('order_items')
                .select('quantity, status')
                .eq('order_id', order.id);
                
              if (!itemsError && itemsData) {
                for (const item of itemsData) {
                  if (item.status === 'delivered') {
                    delivered += item.quantity || 0;
                  } else if (item.status === 'returned') {
                    returned += item.quantity || 0;
                  }
                }
              }
            }
            
            if (customer.stats) {
              customer.stats.delivered_products = delivered;
              customer.stats.returned_products = returned;
            }
          }
        }
        
        setCustomersWithStats(enhancedCustomers);
      } catch (error) {
        console.error('Error fetching customer stats:', error);
      } finally {
        setLoadingStats(false);
        isFetchingRef.current = false;
      }
    };
    
    fetchCustomerStats();
    
    // Nettoyage lors du démontage
    return () => {
      isFetchingRef.current = false;
    };
  }, [open, customers, customersWithStats.length]); // Added customersWithStats.length as dependency

  return {
    customersWithStats,
    loadingStats
  };
};
